import React, {useState, useEffect} from 'react';
import { Box, Typography, Button, Switch, ButtonBase, IconButton, Menu, MenuItem } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import CustomAppBar from '../components/CustomAppBar';
import Navbar from '../components/Navbar';
import AppContainer from '../components/AppContainer';
import TextContainer from '../components/TextContainer';
import SectionBanner from '../components/SectionBanner';
import DeleteIcon from '@mui/icons-material/Delete';
import MemberListItem from '../components/MemberListItem';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { removeGroup, removeGroupFromReduxAndServer, updateGroup, updateGroupOnReduxAndServer } from '../redux/groupSlice';
import { addUserToReduxAndServer } from '../redux/userSlice';
import { updateHeartOnReduxAndServer } from '../redux/heartSlice';
import GroupHeartListItem from '../components/GroupHeartListItem';
import CopyableTextBox from '../components/CopyableTextBox';
import { BiSolidCopy } from 'react-icons/bi';
import SendIcon from '@mui/icons-material/Send';
import ConfirmPopup from '../components/ConfirmPopup';
import LogoutIcon from '@mui/icons-material/Logout';
import ColorModeSwitch from '../components/ColorModeSwitch';
import { ICON_COLOR, OFFLINE_RED, ONLINE_GREEN, PREMIUM_ICON_COLOR, PRIMARY_BUTTON_BLUE, UNDERLINE_ACCENT_COLOR } from '../colorPalette';
import SettingsIcon from '@mui/icons-material/Settings';
import SectionBanner2 from '../components/SectionBanner2';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import NumbersIcon from '@mui/icons-material/Numbers';
import { Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import DiamondIcon from '@mui/icons-material/Diamond';
// api
import { queryServer } from '../api/api';
import { publishMessage } from '../api/mqtt';
import { EMPTY_GROUP_ID, MQTT_TOPIC_PREFIX } from '../constants';
import TextBox from '../components/TextBox';
// Formik validation
import { Formik, Form } from 'formik';
import EditNamePopupFormik from '../components/EditNamePopupFormik';
import { groupNameSchema } from '../validation/schemas';
import { GroupSchema, HeartSchema, UserSchema } from 'lumas-shared-schemas';


function ManageGroup() {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get the user from the store
    const user = useSelector((state) => state.user.user);
    
    // Get the group from the store
    const groups = useSelector((state) => state.group.groups);
    const group = groups.find((group) => group.id === params?.groupId);
    console.log("Group: ", group, "Group ID: ", params?.groupId);

    const hearts = useSelector((state) => state.heart.hearts);
    const groupHearts = useSelector((state) => state.heart.groupHearts);
    const isAdmin = group.members.find((member) => member.email === user.email).admin; // true if the current user is an admin of the group
    
    // Set color mode state based on the group's color mode
    const [mode, setMode] = useState(group.colorMode); 
    const [alertOpen, setAlertOpen] = useState(false); // alert popup

    // Edit Group Name popup
    const [namePopupOpen, setNamePopupOpen] = useState(false);
    const [tempName, setTempName] = useState(group.name);
    const handleNameCancel = () => {
        setNamePopupOpen(false); // Close the popup when clicked outside or on a close button
    };

    const handleColorModeChange = () => {
        // Compute the new mode
        const newMode = mode === "Solid" ? "Multi" : "Solid";
        
        // Update the color mode in the store
        const updatedGroup = GroupSchema.parse({
            ...group, 
            colorMode: newMode
        });
        dispatch(updateGroupOnReduxAndServer({id: group.id, updatedGroup: updatedGroup}));

        // Switch the color mode between Solid and Multi
        setMode(newMode);

        // Send "true" to the MQTT group topic 'group/{groupID}/multicolorMode' if the new mode is "Multi", otherwise send "false" for "Solid"
        const multicolorModeMessage = newMode === "Multi" ? "true" : "false";
        const multicolorModeTopic = `${MQTT_TOPIC_PREFIX}${group.id}/multicolorMode`;
        publishMessage(multicolorModeTopic, multicolorModeMessage, false);
    }

    const handleFormSubmit = (values, {setSubmitting}) => {
        console.log("Values", values);
        setSubmitting(false); // Set submitting to false after delay

        // dispatch the new temp group name in store, validating against schema
        const updatedGroup = GroupSchema.parse({
            ...group, 
            name: values.name
        });
        dispatch(updateGroupOnReduxAndServer({id: group.id, updatedGroup: updatedGroup}));

        setNamePopupOpen(false); // Close the popup

    };
    
    // delete confirmation popup
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const handleDeleteCancel = () => {
        setDeletePopupOpen(false); // Close the popup
    };

    const handleDeleteConfirm = () => {

        // Delete group if the user is an admin, otherwise leave the group
        if (isAdmin) {
            // Delete the group - remove the group from the user's groups list, remove the group from the DB and store
            console.log("Deleting group: ", group.id);

            // 1. update the hearts in the group (groupHearts) to reset their group to EMPTY_GROUP_ID
            group.hearts.map((heartID) => {
                // look up the heart in the groupHearts store, or if not found, in the hearts store
                const heart = groupHearts.find((h) => h.id === heartID) || hearts.find((h) => h.id === heartID);
                const updatedHeart = HeartSchema.parse({
                    ...heart, 
                    group: EMPTY_GROUP_ID
                });
                console.log("Updated heart in group deletion: ", updatedHeart);
                dispatch(updateHeartOnReduxAndServer({id: heart.id, updatedHeart: updatedHeart}));
            });

            // 2. remove the group from the user's groups list, validating against schema
            const updatedUser = UserSchema.parse({
                ...user, 
                groups: user.groups.filter((groupID) => groupID !== group.id)
            });
            dispatch(addUserToReduxAndServer(updatedUser));

            // 3. update the other members' groups list to remove the group ID
            updateOtherGroupMembers(group);

            // 4. remove the group from the DB and store
            dispatch(removeGroupFromReduxAndServer(group.id));
        } else {
            // Leave the group
            console.log("Leaving group: ", group.id);

            // 1. update the user's groups list to remove the group ID, validating against schema
            const updatedUser = UserSchema.parse({
                ...user, 
                groups: user.groups.filter((groupID) => groupID !== group.id)
            });
            dispatch(addUserToReduxAndServer(updatedUser));

            // 2. remove the user from the group
            const updatedGroupMembers = group.members.filter((member) => member.email !== user.email);
            console.log("Updated Members: ", updatedGroupMembers);

            // 3. remove all hearts from group owned by the removed user
            // check if user's hearts in group.hearts; if yes, remove from group and update the heart's group to EMPTY_GROUP_ID
            let updatedGroupHeartIDs = group.hearts;
            hearts.map((heart) => {
                if (group.hearts.includes(heart.id)) {
                    console.log("removing heart from group: ", heart.id);
                    // 3. remove heart from group.hearts list
                    updatedGroupHeartIDs = group.hearts.filter((heartID) => heartID !== heart.id);
                    // 4. update the heart's group to EMPTY_GROUP_ID, validating against schema
                    const updatedHeart = HeartSchema.parse({
                        ...heart, 
                        group: EMPTY_GROUP_ID
                    });
                    dispatch(updateHeartOnReduxAndServer({id: heart.id, updatedHeart: updatedHeart}));
                }
            });

            // remove group from the Redux groups array (local Redux store ONLY because the server will still have the group until an admin deletes it).
            dispatch(removeGroup(group.id));


            const updatedGroup = GroupSchema.parse({
                ...group, 
                members: updatedGroupMembers,
                hearts: updatedGroupHeartIDs
            })
            console.log("updated group: ", updateGroup);

            dispatch(updateGroupOnReduxAndServer({id: group.id, updatedGroup }));
        }

        navigate('/home');
    }

    const updateOtherGroupMembers = async (group) => {
        const promises = group.members.map(async (member) => {
            if (member.email !== user.email) {
                console.log("Updated member: ", member.email);
                const userDataResponse = await queryServer(member.email, 'users');
                console.log("User data from server: ", userDataResponse);

                // remove the group ID from the user's groups list, validating against schema
                const updatedOtherUser = UserSchema.parse({
                    ...userDataResponse,
                    groups: userDataResponse.groups.filter((groupID) => groupID !== group.id)
                });
                dispatch(addUserToReduxAndServer(updatedOtherUser));
            }
        });
    
        await Promise.all(promises);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(group.id);
        setAlertOpen(true);
    };

    const handleCloseAlert = () => {
        setAlertOpen(false);
    };

    useEffect(() => {
        // Scroll to the top of the page when the component mounts
        window.scrollTo(0, 0);
      }, []);

    return (
        <AppContainer>
            {deletePopupOpen && (
                // Delete confirmation popup for admins, Leave confirmation popup for non-admins
                isAdmin ? <ConfirmPopup confirmText={"Delete"} title={"Are you sure you want to delete this group?"} subtitle={"(Admin status must be transferred first if you wish to simply leave the group)."} handleCancel={handleDeleteCancel} handleConfirm={handleDeleteConfirm} /> 
                : <ConfirmPopup confirmText={"Leave"} title={"Are you sure you want to leave this group?"} handleCancel={handleDeleteCancel} handleConfirm={handleDeleteConfirm} />
            )}
            {namePopupOpen && (
                <Formik
                    initialValues={{ name: group.name}}
                    validationSchema={groupNameSchema}
                    onSubmit={handleFormSubmit}
                >
                        {({ isSubmitting, errors, touched }) => (
                            <Form>
                                <EditNamePopupFormik errors={errors} touched={touched} title={"Edit Group Name:"} handleCancel={handleNameCancel} />
                            </Form>
                        )}
                    </Formik>
            )}
            <CustomAppBar />

            {/* Alert popup */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={3000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
                    Group ID copied to clipboard.
                </Alert>
            </Snackbar>

            <Navbar title={"Group Settings"} 
                // Delete button for admins, Leave button for non-admins
                rightButtonIcon={isAdmin ? <DeleteIcon sx={{color: 'red'}} /> : <LogoutIcon sx={{color: 'red'}} />} 
                rightButtonOnClick={() => {setDeletePopupOpen(true)}} 
            />
            <TextContainer>
                <Typography variant='h5' sx={{marginTop: '10px'}}>{group.name}</Typography>
                <Button onClick={() => {setNamePopupOpen(true);}} variant='text' color='primary' sx={{textAlign: 'left', padding: 0, fontWeight:'Regular', marginTop: '-15px'}}>Edit Name</Button>
            </TextContainer>
            <SectionBanner2 text={"Group ID"} IconComponent={NumbersIcon} customStyles={{marginTop: '10px'}} />
            <TextContainer>
                <Box sx={{display: "flex", flexDirection: 'row'}}>
                    {/* <Typography variant="body2" sx={{alignContent: 'center'}}>Group ID:</Typography> */}
                    {/* <CopyableTextBox propText={group.id} customStyles={{marginLeft: '20px'}} /> */}
                    <TextBox propText={group.id} customStyles={{marginLeft: '20px'}} />
                    <IconButton onClick={handleCopy} sx={{marginLeft: '10px', color: PRIMARY_BUTTON_BLUE}}>
                        <BiSolidCopy />
                    </IconButton>
                </Box>
                <Typography variant='body2' fontStyle={'italic'} sx={{fontSize: '11px', marginTop: '15px', textAlign: 'center'}}>Click the copy button to copy the Group ID, then send it to the people you want to join your group.</Typography>
            </TextContainer>

            <SectionBanner2 text={"Group Members"} IconComponent={GroupsIcon} />
            <TextContainer>
                {group.members.map((member, index) => (
                    <MemberListItem 
                        key={index} 
                        email={member.email} 
                        admin={member.admin} 
                        // TODO: show admin menu on non-admin MemberListItems, only if the current user is an admin
                        adminMenu={group.members.find((member) => member.email === user.email).admin ? true : false}
                    />
                ))}
            </TextContainer>

            {/* <SectionBanner2 text={"Hearts"} IconComponent={FavoriteIcon} />
            <TextContainer>
                {group.hearts.length === 0 && 
                <Box sx={{width: '100%', textAlign: 'center'}}>
                    <Typography variant='body2' fontSize={'11px'} fontStyle={'italic'}>No hearts in this group. Navigate to your hearts and click 'Add to Group' to add a heart to this group.</Typography>
                </Box> }
                {group.hearts.map((heartID, index) => {
                    // look up the heart in the groupHearts store, if not found, look up in the hearts store
                    const heart = groupHearts.find((h) => h.id === heartID) || hearts.find((h) => h.id === heartID);
                    return (
                        <GroupHeartListItem key={index} name={heart.name} owner={heart.owner} color={heart.status === "Online" ? ONLINE_GREEN : OFFLINE_RED} />
                    )
                })}
            </TextContainer> */}

            <SectionBanner2 text={"Group Settings"} IconComponent={SettingsIcon} />
            <TextContainer>
                <Box sx={{display: 'flex', alignItems: 'center', marginTop: '10px', width: '100%', justifyContent: 'space-between'}}>
                    <Box sx={{display: 'flex', flexDirection: 'row'}} >
                        <Typography variant='body2' sx={{marginRight: '5px'}} >Color Mode: </Typography>
                        <DiamondIcon sx={{color: PREMIUM_ICON_COLOR}} />
                    </Box>
                    <ColorModeSwitch mode={mode} onClick={handleColorModeChange} sx={{marginLeft: '10px'}} />
                </Box>
            {/* Space to add buffer to bottom */}
            <Box sx={{height: '100px'}}></Box>
            </TextContainer>

        </AppContainer>
    )
}

export default ManageGroup